import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import usePaymentVerification from "@/hooks/usePaymentVerification";
import useSEO from "@/hooks/useSEO";

function Checking() {
  return (
    <div data-testid="payment-checking">
      <Loader2 size={44} className="text-gold mx-auto mb-5 animate-spin" />
      <h1 className="font-playfair text-2xl font-bold text-brown-dark mb-2">Confirming Your Payment...</h1>
      <p className="text-gray-500 text-sm">Hang tight — this only takes a few seconds.</p>
    </div>
  );
}

function Paid({ isSubscription }) {
  return (
    <div data-testid="payment-paid">
      <CheckCircle size={44} className="text-gold mx-auto mb-5" />
      <h1 className="font-playfair text-3xl font-bold text-brown-dark mb-3">{isSubscription ? "Subscription Active!" : "Order Confirmed!"}</h1>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">
        {isSubscription
          ? "Welcome to the ritual. Your first 7-pack ships now, with weekly deliveries at your subscription price. Manage or cancel anytime from your PayPal account."
          : "Thank you for choosing Ken's Gold. Your shots are on their way — check your email for the PayPal receipt. Here's to your daily ritual."}
      </p>
      <Link to="/shop" className="btn-gold" data-testid="back-to-shop-btn">Continue Shopping</Link>
    </div>
  );
}

function Failed({ retry }) {
  return (
    <div data-testid="payment-failed">
      <AlertCircle size={44} className="text-red-400 mx-auto mb-5" />
      <h1 className="font-playfair text-2xl font-bold text-brown-dark mb-2">Payment Not Completed</h1>
      <p className="text-gray-500 text-sm mb-8">We couldn’t confirm this checkout. Check your PayPal activity before placing another order, or contact us with your order reference.</p>
      <button onClick={retry} className="text-sm text-gold underline block mx-auto mb-5" data-testid="payment-failed-retry">Check this payment again</button>
      <Link to="/shop" className="btn-gold" data-testid="retry-shop-btn">Back to Shop</Link>
    </div>
  );
}

function Timeout({ retry }) {
  return (
    <div data-testid="payment-timeout">
      <AlertCircle size={44} className="text-gold mx-auto mb-5" />
      <h1 className="font-playfair text-2xl font-bold text-brown-dark mb-2">Still Processing</h1>
      <p className="text-gray-500 text-sm mb-8">Your payment is awaiting confirmation. Check this payment again before starting another order.</p>
      <button onClick={retry} className="btn-gold" data-testid="payment-confirmation-retry">Check payment again</button>
      <a href="mailto:orders@kensgold.com?subject=Payment%20confirmation" className="block text-sm text-gold underline mt-5" data-testid="payment-help-link">Contact us about this payment</a>
    </div>
  );
}

const VIEWS = { checking: Checking, paid: Paid, failed: Failed, error: Failed, timeout: Timeout };

export default function PaymentSuccess() {
  useSEO({ title: "Payment Confirmation", description: "Your Ken’s Gold payment confirmation." });
  const [params] = useSearchParams();
  const isSubscription = params.get("type") === "subscription";
  const id = isSubscription ? params.get("subscription_id") : params.get("token");
  const { state, retry } = usePaymentVerification(id, isSubscription);
  const View = VIEWS[state];

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-20" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gold/15 p-10 shadow-sm">
        <View isSubscription={isSubscription} retry={retry} />
      </div>
    </div>
  );
}
